import React, { useState, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import {
  grayColor,
  dangerColor,
} from "assets/jss/material-dashboard-pro-react";

import Button from "components/CustomButtons/Button";
import ModalHeader from "components/ModalHeader";
import modalStyle from "assets/jss/material-dashboard-pro-react/modalStyle";
import useThemeColors from "hooks/useThemeColors";

const useStyles = makeStyles((theme) => ({
  ...modalStyle(theme),
  // Floating trigger button
  triggerButton: {
    position: "fixed",
    right: 0,
    top: "85%",
    transform: "translateY(-50%)",
    zIndex: 1000,
    backgroundColor: grayColor[6],
    color: "#fff",
    padding: "10px 1px 10px 0",
    borderRadius: "8px 0 0 8px",
    cursor: "pointer",
    boxShadow: "-2px 2px 8px rgba(0,0,0,0.15)",
    transition: "all 0.2s ease",
    "&:hover": {
      paddingRight: "10px",
    },
  },
  triggerButtonHighlight: {
    paddingRight: "10px",
  },
  triggerButtonText: {
    writingMode: "vertical-rl",
    textOrientation: "mixed",
    transform: "rotate(180deg)",
    fontSize: "13px",
    fontWeight: 500,
    whiteSpace: "nowrap",
    letterSpacing: "0.5px",
  },
  // Overlay
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    zIndex: 1100,
    opacity: 0,
    visibility: "hidden",
    transition: "opacity 0.3s ease, visibility 0.3s ease",
  },
  overlayOpen: {
    opacity: 1,
    visibility: "visible",
  },
  // Panel container
  panelContainer: {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    width: "360px",
    maxWidth: "90vw",
    zIndex: 1101,
    display: "flex",
    flexDirection: "column",
    padding: "20px",
    paddingTop: "60px",
    gap: "16px",
    overflowY: "auto",
    transform: "translateX(100%)",
    transition: "transform 0.3s ease",
  },
  panelContainerOpen: {
    transform: "translateX(0)",
  },
  // Close button
  closeButton: {
    position: "fixed",
    top: "16px",
    right: "16px",
    zIndex: 1102,
    backgroundColor: grayColor[6],
    color: "#fff",
    padding: "6px",
    borderRadius: "50%",
    opacity: 0,
    visibility: "hidden",
    transition:
      "opacity 0.3s ease, visibility 0.3s ease, background-color 0.2s",
    "&:hover": {
      backgroundColor: grayColor[4],
    },
  },
  closeButtonOpen: {
    opacity: 1,
    visibility: "visible",
  },
  // Message cards (floating, no paper background)
  messageCard: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "16px",
    // paddingRight: "12px",
    boxShadow: "-4px 4px 16px rgba(0,0,0,0.15)",
    position: "relative",
  },
  messageTitle: {
    fontSize: "14px",
    fontWeight: 600,
    marginBottom: "8px",
    color: grayColor[1],
    paddingRight: "24px",
  },
  messageText: {
    fontSize: "13px",
    lineHeight: 1.5,
    color: grayColor[2],
    "& b, & strong": {
      fontWeight: 600,
    },
    "& i, & em": {
      fontStyle: "italic",
    },
    "& u": {
      textDecoration: "underline",
    },
  },
  messageEditButton: {
    position: "absolute",
    top: "8px",
    right: "4px",
    padding: "4px",
    color: grayColor[2],
    opacity: 0.6,
    transition: "opacity 0.2s",
    "&:hover": {
      opacity: 1,
      backgroundColor: "transparent",
    },
  },
  // Add button
  addButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "fit-content",
    alignSelf: "center",
    padding: "8px 16px",
    fontSize: "12px",
    color: "#fff",
    backgroundColor: grayColor[7] + "!important",
    border: "none",
    borderRadius: "8px !important",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: grayColor[6] + "!important",
    },
  },
  // Empty state
  emptyState: {
    backgroundColor: "rgba(255,255,255,0.9)",
    borderRadius: "8px 0 0 8px",
    padding: "24px 16px",
    textAlign: "center",
    color: grayColor[3],
    fontSize: "14px",
  },
}));

// Simple text editor with bold, italic and underline options
const SimpleTextEditor = React.memo(function SimpleTextEditor({
  initialContent,
  onContentChange,
}) {
  const [editorState, setEditorState] = useState(() => {
    if (initialContent) {
      const blocksFromHtml = htmlToDraft(initialContent);
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(
        contentBlocks,
        entityMap,
      );
      return EditorState.createWithContent(contentState);
    }
    return EditorState.createEmpty();
  });

  const onContentChangeRef = useRef(onContentChange);
  onContentChangeRef.current = onContentChange;

  const handleEditorStateChange = useCallback((newState) => {
    setEditorState(newState);
    onContentChangeRef.current(
      draftToHtml(convertToRaw(newState.getCurrentContent())),
    );
  }, []);

  return (
    <Editor
      editorState={editorState}
      onEditorStateChange={handleEditorStateChange}
      wrapperStyle={{
        border: `1px solid ${grayColor[8]}`,
        borderRadius: 4,
      }}
      toolbarStyle={{
        border: "none",
        borderBottom: `1px solid ${grayColor[8]}`,
        marginBottom: 0,
        padding: "4px 8px",
      }}
      editorStyle={{
        fontSize: 14,
        lineHeight: 1.5,
        minHeight: 120,
        padding: "8px 12px",
        backgroundColor: grayColor[12],
      }}
      toolbar={{
        options: ["inline"],
        inline: {
          options: ["bold", "italic", "underline"],
        },
      }}
    />
  );
});

SimpleTextEditor.propTypes = {
  initialContent: PropTypes.string,
  onContentChange: PropTypes.func.isRequired,
};

/**
 * KeyMessagesPanel - Floating panel for section key messages
 */
function KeyMessagesPanel({ messages: initialMessages, onMessagesChange }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { clientColor, clientTextColor } = useThemeColors();

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(initialMessages || []);
  const [isHighlighted, setIsHighlighted] = useState(false);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editorKey, setEditorKey] = useState(0);

  const titleInputRef = useRef(null);

  // Handle ESC key to close panel
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && isOpen && !editModalOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, editModalOpen]);

  // Sync messages with parent
  useEffect(() => {
    if (onMessagesChange) {
      onMessagesChange(messages);
    }
  }, [messages, onMessagesChange]);

  // Highlight trigger button on mount if there are messages
  useEffect(() => {
    if (initialMessages && initialMessages.length > 0) {
      setIsHighlighted(true);
      const timer = setTimeout(() => {
        setIsHighlighted(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleEditMessage = useCallback(
    (index) => {
      setEditingIndex(index);
      setEditTitle(messages[index].title);
      setEditContent(messages[index].text);
      setEditorKey((prev) => prev + 1);
      setEditModalOpen(true);
    },
    [messages],
  );

  const handleAddMessage = useCallback(() => {
    setEditingIndex(null);
    setEditTitle("");
    setEditContent("");
    setEditorKey((prev) => prev + 1);
    setEditModalOpen(true);
  }, []);

  const handleSaveMessage = useCallback(() => {
    if (editingIndex !== null) {
      setMessages((prev) =>
        prev.map((msg, idx) =>
          idx === editingIndex ? { title: editTitle, text: editContent } : msg,
        ),
      );
    } else {
      setMessages((prev) => [...prev, { title: editTitle, text: editContent }]);
    }
    setEditModalOpen(false);
  }, [editingIndex, editTitle, editContent]);

  const handleCloseModal = useCallback(() => {
    setEditModalOpen(false);
  }, []);

  const handleDeleteMessage = useCallback(() => {
    if (editingIndex !== null) {
      setMessages((prev) => prev.filter((_, idx) => idx !== editingIndex));
      setEditModalOpen(false);
    }
  }, [editingIndex]);

  const handleContentChange = useCallback((content) => {
    setEditContent(content);
  }, []);

  return (
    <>
      {/* Floating trigger button */}
      <div
        className={`${classes.triggerButton} ${isHighlighted ? classes.triggerButtonHighlight : ""}`}
        onClick={handleOpen}
      >
        <span className={classes.triggerButtonText}>
          {t("alignment_session.key_messages.trigger_button", {
            count: messages.length,
            defaultValue: `Mensagens (${messages.length})`,
          })}
        </span>
      </div>

      {/* Overlay */}
      <div
        className={`${classes.overlay} ${isOpen ? classes.overlayOpen : ""}`}
        onClick={handleClose}
      />

      {/* Close button */}
      <IconButton
        className={`${classes.closeButton} ${isOpen ? classes.closeButtonOpen : ""}`}
        onClick={handleClose}
        size="small"
      >
        <CloseIcon style={{ fontSize: 20 }} />
      </IconButton>

      {/* Panel with message cards */}
      <div
        className={`${classes.panelContainer} ${isOpen ? classes.panelContainerOpen : ""}`}
      >
        {messages.length === 0 ? (
          <div className={classes.emptyState}>
            {t("alignment_session.key_messages.empty", {
              defaultValue: "Nenhuma mensagem adicionada",
            })}
          </div>
        ) : (
          messages.map((msg, index) => (
            <div key={index} className={classes.messageCard}>
              <IconButton
                size="small"
                className={classes.messageEditButton}
                onClick={() => handleEditMessage(index)}
              >
                <EditIcon style={{ fontSize: 16 }} />
              </IconButton>
              <div className={classes.messageTitle}>{msg.title}</div>
              <div
                className={classes.messageText}
                dangerouslySetInnerHTML={{ __html: msg.text }}
              />
            </div>
          ))
        )}

        {/* Add button */}
        <Button className={classes.addButton} onClick={handleAddMessage}>
          <AddIcon style={{ fontSize: 18, marginRight: 4 }} />
          {t("alignment_session.key_messages.add", {
            defaultValue: "Adicionar",
          })}
        </Button>
      </div>

      {/* Edit/Add Message Modal */}
      <Dialog
        classes={{
          root: classes.modalRoot,
          paper: classes.modal,
        }}
        open={editModalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <ModalHeader
          title={
            editingIndex !== null
              ? t("alignment_session.key_messages.edit_message", {
                  defaultValue: "Editar mensagem",
                })
              : t("alignment_session.key_messages.add_message", {
                  defaultValue: "Adicionar mensagem",
                })
          }
          onClose={handleCloseModal}
          closeLabel={t("common.close")}
          clientColor={clientColor}
        />
        <DialogContent className={classes.modalBody}>
          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                display: "block",
                marginBottom: 4,
                fontWeight: 500,
                fontSize: 13,
              }}
            >
              {t("alignment_session.key_messages.title_label", {
                defaultValue: "Título",
              })}
            </label>
            <input
              ref={titleInputRef}
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              autoFocus
              style={{
                width: "100%",
                padding: "8px 12px",
                border: `1px solid ${grayColor[8]}`,
                borderRadius: 4,
                fontSize: 14,
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: 4,
                fontWeight: 500,
                fontSize: 13,
              }}
            >
              {t("alignment_session.key_messages.content_label", {
                defaultValue: "Conteúdo",
              })}
            </label>
            <SimpleTextEditor
              key={editorKey}
              initialContent={editContent}
              onContentChange={handleContentChange}
            />
          </div>
        </DialogContent>
        <DialogActions
          className={classes.modalFooter}
          style={{ justifyContent: "space-between" }}
        >
          <div>
            {editingIndex !== null && (
              <Button
                style={{
                  backgroundColor: dangerColor[0],
                  color: "#fff",
                }}
                onClick={handleDeleteMessage}
              >
                {t("common.delete", { defaultValue: "Excluir" })}
              </Button>
            )}
          </div>
          <div>
            <Button color="transparent" onClick={handleCloseModal}>
              {t("common.cancel", { defaultValue: "Cancelar" })}
            </Button>
            <Button
              style={{
                backgroundColor: clientColor,
                color: clientTextColor,
              }}
              onClick={handleSaveMessage}
              disabled={!editTitle.trim()}
            >
              {t("common.save", { defaultValue: "Salvar" })}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
}

KeyMessagesPanel.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    }),
  ),
  onMessagesChange: PropTypes.func,
};

KeyMessagesPanel.defaultProps = {
  messages: [],
  onMessagesChange: null,
};

export default KeyMessagesPanel;
